import {
  StandardFieldValidationMessageModel,
} from 'services/models/corporate/approval-scope/standard-field-validation.model';

export interface MovementRequestToastHandler {
  showWarning(message: string, position?: string): void;
  showError(message: string, position?: string): void;
}

export interface HandleMovementRequestCommandOutcomeOptions {
  response?: unknown;
  error?: unknown;
  toast: MovementRequestToastHandler;
  onSuccess: () => void;
  onBlocked?: () => void;
  toastPosition?: string;
  blockedFallbackMessage?: string;
}

export function extractStandardFieldValidationAlerts(
  response: unknown
): StandardFieldValidationMessageModel[] {
  const body = (response as { body?: unknown })?.body ?? response;
  const alerts = (body as { standardFieldValidationAlerts?: unknown[] })?.standardFieldValidationAlerts;

  if (Array.isArray(alerts)) {
    return alerts.map(item => StandardFieldValidationMessageModel.create(item as object));
  }

  return extractValidationResultMessagesBySeverity(response, 'warning');
}

export function extractStandardFieldValidationBlocksFromResponse(
  response: unknown
): StandardFieldValidationMessageModel[] {
  return extractValidationResultMessagesBySeverity(response, 'error');
}

function extractValidationResultMessagesBySeverity(
  response: unknown,
  severity: 'warning' | 'error'
): StandardFieldValidationMessageModel[] {
  const body = (response as { body?: unknown })?.body ?? response;
  const errors = (body as {
    errors?: Array<{ propertyName?: string; errorMessage?: string; severity?: string | number }>;
  })?.errors;

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors
    .filter(error => normalizeValidationSeverity(error.severity) === severity)
    .map(error =>
      StandardFieldValidationMessageModel.create({
        validationKey: error.propertyName,
        message: error.errorMessage,
      })
    );
}

function normalizeValidationSeverity(value?: string | number): 'warning' | 'error' {
  if (value === null || value === undefined || value === '') {
    return 'error';
  }

  if (typeof value === 'number') {
    return value === 1 ? 'warning' : 'error';
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'warning' || normalized === '1') {
    return 'warning';
  }

  return 'error';
}

export function extractStandardFieldValidationBlockMessages(error: unknown): string[] {
  const apiErrors = (error as { error?: { errors?: Record<string, unknown> } })?.error?.errors;
  const messages = apiErrors?.Messages ?? apiErrors?.messages;

  if (Array.isArray(messages)) {
    return messages.filter((message): message is string => typeof message === 'string' && !!message);
  }

  if (typeof messages === 'string' && messages) {
    return [messages];
  }

  return [];
}

export function formatStandardFieldValidationAlertMessages(
  alerts: StandardFieldValidationMessageModel[]
): string {
  return alerts.map(alert => alert.message).filter(Boolean).join('\n');
}

export function handleMovementRequestCommandOutcome(
  options: HandleMovementRequestCommandOutcomeOptions
): boolean {
  const {
    response,
    error,
    toast,
    onSuccess,
    onBlocked,
    toastPosition = 'top-right',
    blockedFallbackMessage = 'Não foi possível concluir a operação. Verifique a causa e tente novamente.',
  } = options;

  if (error) {
    const blockMessages = extractStandardFieldValidationBlockMessages(error);
    toast.showError(
      blockMessages.length ? blockMessages.join('\n') : blockedFallbackMessage,
      toastPosition
    );
    onBlocked?.();
    return false;
  }

  const blocks = extractStandardFieldValidationBlocksFromResponse(response);
  if (blocks.length) {
    toast.showError(formatStandardFieldValidationAlertMessages(blocks), toastPosition);
    onBlocked?.();
    return false;
  }

  const alerts = extractStandardFieldValidationAlerts(response);
  if (alerts.length) {
    toast.showWarning(formatStandardFieldValidationAlertMessages(alerts), toastPosition);
  }

  onSuccess();
  return true;
}
